import { format } from "date-fns";
import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";
import React from "react";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";

// const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const initials = (firstName, lastName) => {
  return `${firstName?.charAt(0)}${lastName?.charAt(0)}`;
};

export const fmtCurrency = (value, currency) => {
  const formatter = new Intl.NumberFormat("en-NG", {
    style: "currency",
    currencyDisplay: "symbol",
    currency: currency ? currency : "NGN",
  });
  return formatter.format(Number(value));
};

export function getQueryVariable(variable) {
  var query = window.location.search.substring(1);
  var vars = query.split("&");
  for (var i = 0; i < vars.length; i++) {
    var pair = vars[i].split("=");
    if (pair[0] === variable) {
      return pair[1];
    }
  }
  return false;
}

export const dateFormat = (date, type) => {
  if (!date) return "--";
  return format(new Date(date), type);
};

// Format timestamp without timezone conversion (for database timestamps stored as 'timestamp without timezone')
export const dateFormatLiteral = (timestamp, type) => {
  if (!timestamp) return "--";

  try {
    let dateStr = timestamp.toString();

    // Handle different timestamp formats
    // Convert ISO format to space-separated format: "2025-09-21T00:45:00.000Z" -> "2025-09-21 00:45:00"
    if (dateStr.includes('T')) {
      dateStr = dateStr.replace('T', ' ');
    }

    // Remove timezone info and milliseconds
    dateStr = dateStr.replace(/Z.*$/, ''); // Remove Z and anything after
    dateStr = dateStr.replace(/\+\d{2}:\d{2}.*$/, ''); // Remove timezone offset like +01:00
    dateStr = dateStr.replace(/-\d{2}:\d{2}.*$/, ''); // Remove timezone offset like -05:00
    dateStr = dateStr.replace(/\.\d{3}.*$/, ''); // Remove milliseconds

    // Ensure we have both date and time parts
    const parts = dateStr.split(' ');
    if (parts.length < 2) {
      console.warn('Invalid timestamp format:', timestamp);
      return dateFormat(timestamp, type); // Fallback to original function
    }

    const [datePart, timePart] = parts;
    const [year, month, day] = datePart.split('-').map(Number);
    const timeComponents = timePart.split(':').map(Number);
    const [hours, minutes, seconds = 0] = timeComponents;

    // Validate parsed values
    if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hours) || isNaN(minutes)) {
      console.warn('Invalid date components:', { year, month, day, hours, minutes });
      return dateFormat(timestamp, type); // Fallback to original function
    }

    // Create date object in local timezone (no conversion)
    const date = new Date(year, month - 1, day, hours, minutes, seconds);

    return format(date, type);
  } catch (error) {
    console.warn('Error parsing timestamp:', timestamp, error);
    return dateFormat(timestamp, type); // Fallback to original function
  }
};

export function convertLocalToUTC(localDate) {
  return localDate.toISOString();
}

export function convertUTCToLocal(utcDateString) {
  if (!utcDateString) return null;
  // Parse UTC date string and convert to local time
  const utcDate = new Date(utcDateString + (utcDateString.includes('Z') ? '' : 'Z'));
  return utcDate;
}

// Parse a UTC timestamp from the API ("...Z", "...+00:00" or no zone suffix)
// into a local Date. Returns null when missing or unparseable.
export function parseDbTimestamp(timestamp) {
  if (!timestamp) return null;
  if (typeof timestamp !== "string") {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }
  const normalized = timestamp.replace("+00:00", "Z");
  let date = convertUTCToLocal(normalized);
  if (!date || isNaN(date.getTime())) {
    date = new Date(normalized);
  }
  return isNaN(date.getTime()) ? null : date;
}

export const formatUTCForDB = (date, type = "yyyy-MM-dd HH:mm:ss") => {
  if (!date) return null;
  return formatInTimeZone(new Date(date), "UTC", type);
};

// Shift times are entered and displayed in UK local time (Europe/London),
// regardless of the viewer's device timezone; persistence stays UTC.
// BST/GMT transitions are handled by the IANA timezone database.
export const UK_TIMEZONE = "Europe/London";

// Format a stored UTC timestamp (string or Date) as UK wall time
export const formatUKTime = (timestamp, type) => {
  const date = parseDbTimestamp(timestamp);
  if (!date) return "--";
  return formatInTimeZone(date, UK_TIMEZONE, type);
};

// Interpret a picker Date's wall-clock fields as UK time and return the
// corresponding UTC instant (for building API payloads)
export function ukWallTimeToUTC(date) {
  if (!date) return null;
  return fromZonedTime(date, UK_TIMEZONE);
}

// Convert a stored UTC timestamp into a Date whose wall-clock fields show
// UK time (for prefilling pickers)
export function toUKWallTime(timestamp) {
  const date = parseDbTimestamp(timestamp);
  return date ? toZonedTime(date, UK_TIMEZONE) : null;
}

export const formatTime = (time) => {
  if (!time) return "--";
  const date = new Date();
  const [hours, minutes] = time.split(":");
  date.setHours(parseInt(hours), parseInt(minutes), 0);

  return format(date, "HH:mm");
};

export const dateDefaultValue = (date) => {
  return date ? new Date(date).toISOString().split("T")[0] : "";
};

export function capitalize(text) {
  if (!text) return;
  return text?.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export function capitalizeFirstLetterSentence(sentence) {
  if (!sentence) return;
  return sentence.replace(/(^\w{1})|(\s+\w{1})/g, (letter) =>
    letter.toUpperCase()
  );
}

export function truncateString(str, limit) {
  if (str.length > limit) {
    return str.substring(0, limit) + "...";
  } else {
    return str;
  }
}

export const phoneRegExp =
  /((^090)([0-9]))|((^070)([0-9]))|((^080)([0-9]))|((^091)([0-9]))|((^071)([0-9]))|((^081)([0-9]))(\d{7})/;

export function fullName(firstname, lastname) {
  return `${firstname} ${lastname}`;
}

export const copyText = async (id) => {
  const text = document.getElementById(id);
  await navigator.clipboard.writeText(text.innerText);
  toast.success("Text copied!");
};

export const useQuery = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

export function daysLeft(targetDate) {
  // Convert the target date string to a Date object
  const targetDateTime = new Date(targetDate);

  // Get the current date
  const currentDate = new Date();

  // Convert both dates to UTC to ensure consistency
  const utcCurrentDate = Date.UTC(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate()
  );
  const utcTargetDate = Date.UTC(
    targetDateTime.getFullYear(),
    targetDateTime.getMonth(),
    targetDateTime.getDate()
  );

  // Calculate the difference in milliseconds
  const timeDifference = utcTargetDate - utcCurrentDate;

  // Convert the difference to days
  const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

  return daysDifference;
}

export const arrangeNamesAlphabetically = (data) => {
  const sortedData = [...data].sort((a, b) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();

    if (nameA < nameB) {
      return -1;
    }

    if (nameA > nameB) {
      return 1;
    }

    return 0;
  });

  return sortedData;
};
export const limitString = (str, limit) => {
  return str.length > limit ? str.substring(0, limit) + "..." : str;
};

export const returnDuration = (duration) => {
  switch (duration.toLowerCase()) {
    case "monthly":
      return "month";
    case "quarterly":
      return "quarter";
    case "annually":
      return "year";
    default:
      return "month";
  }
};

export const truncate = (text, length, clamp) => {
  clamp = clamp || "...";
  const node = document.createElement("div");
  node.innerHTML = text;
  const content = node.textContent;
  return content.length > length ? content.slice(0, length) + clamp : content;
};

export const totalHours = (startTime, endTime) => {
  const start = new Date(startTime);
  const end = new Date(endTime);

  const diffInMs = end - start; // Difference in milliseconds
  const totalMinutes = Math.floor(diffInMs / (1000 * 60)); // Convert to minutes

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return { hours, minutes };
};

export const totalShiftHours = (shifts, type) => {
  return shifts.reduce(
    (acc, shift) => {
      const { hours, minutes } =
        type === "clocked"
          ? totalHours(shift.clockedIn, shift.clockedOut)
          : totalHours(shift.shiftStart, shift.shiftEnd);
      acc.hours += hours;
      acc.minutes += minutes;
      return acc;
    },
    { hours: 0, minutes: 0 }
  );
};

// Normalize total minutes into proper hour-minute format
export const formatTotalShiftHours = (shifts, type) => {
  const { hours, minutes } = totalShiftHours(shifts, type);

  const extraHours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return `${hours + extraHours}h ${remainingMinutes}m`;
};

export const getFileIconClass = (extension) => {
  switch (extension.toLowerCase()) {
    case "pdf":
      return "fa-file-pdf";
    case "doc":
    case "docx":
      return "fa-file-word";
    case "xls":
    case "xlsx":
      return "fa-file-excel";
    case "jpg":
    case "jpeg":
    case "png":
      return "fa-file-image";
    default:
      return "fa-file";
  }
};
